import { OmsOrderStatus, OmsOrderType, OrderIntent, Side } from '@/connect';
import { VenueOrderForOms } from '@/connect/gen/modules/apiengine/services/venue/venue_pb';
import {
  agreementPopoverAdapter,
  formatSideAdapter,
  orderIntentAdapter,
  orderTypeAdapter,
  quantity,
  rateAdapter,
} from '@/modules/common/components/ag-table/columns/common';
import { getStatusAdapter } from '@/modules/common/components/ag-table/columns/marketplace';
import {
  alignLeft,
  alignRight,
  comparator,
} from '@/modules/common/components/ag-table/columns/utils';
import { enumValueFormatter } from '@/modules/common/components/ag-table/columns/value-formatters';
import { formatPrettyNumber } from '@/modules/common/components/pretty-number';
import { getVenueStatus } from '@/modules/marketplace/helpers/marketplace';
import { ColDef } from 'ag-grid-enterprise';

export function status(): ColDef<VenueOrderForOms, OmsOrderStatus> {
  return {
    field: 'orderStatus',
    colId: 'orderStatus',
    headerName: 'Status',
    ...alignLeft(),
    ...enumValueFormatter(OmsOrderStatus),
    cellRendererSelector: (params) => {
      return getStatusAdapter<VenueOrderForOms>()({
        order: params.data!,
        ...getVenueStatus(params.data!),
      });
    },
  };
}

export function orderType(): ColDef<VenueOrderForOms> {
  return {
    field: 'orderType',
    colId: 'orderType',
    headerName: 'Type',
    ...enumValueFormatter(OmsOrderType),
    cellRendererSelector: (params) => {
      return orderTypeAdapter({
        orderType: OmsOrderType[params.value],
      });
    },
    ...alignLeft(),
  };
}

export function orderIntent(): ColDef<VenueOrderForOms> {
  return {
    field: 'orderIntent',
    colId: 'orderIntent',
    headerName: 'Intent',
    ...enumValueFormatter(OrderIntent),
    cellRendererSelector: (params) => {
      return orderIntentAdapter({
        orderIntent: OrderIntent[params.value],
      });
    },
    ...alignLeft(),
  };
}

export function side(): ColDef<VenueOrderForOms, Side | undefined> {
  return {
    field: 'side',
    colId: 'side',
    headerName: 'Side',
    ...enumValueFormatter(Side),
    cellRendererSelector: (params) =>
      formatSideAdapter({
        side: params.value === Side.BORROW ? 'BORROW' : 'LOAN',
      }),
    ...alignLeft(),
  };
}

export function ticker(): ColDef<VenueOrderForOms> {
  return {
    field: 'ticker',
    colId: 'ticker',
    headerName: 'Ticker',
    ...alignLeft(),
  };
}

export const openQuantity = (): ColDef<VenueOrderForOms> =>
  quantity({
    field: 'openQuantity',
    colId: 'openQuantity',
    headerName: 'Open Qty',
  });

export const totalQuantity = (): ColDef<VenueOrderForOms> =>
  quantity({
    field: 'totalQuantity',
    colId: 'totalQuantity',
    headerName: 'Total Qty',
  });

export const execQuantity = (): ColDef<VenueOrderForOms> =>
  quantity({
    field: 'totalExecutionQuantity',
    colId: 'totalExecutionQuantity',
    headerName: 'Exec Qty',
  });

export function fee(): ColDef<VenueOrderForOms> {
  return {
    field: 'fee',
    colId: 'fee',
    headerName: 'Fee',
    cellRendererSelector: (params) =>
      rateAdapter({
        rate: params.value,
        rateModifier: undefined,
      }),
    comparator: comparator.decimal,
    cellDataType: 'text',
    ...alignRight({ hasPostfix: true }),
  };
}

export function rebate(): ColDef<VenueOrderForOms> {
  return {
    field: 'rebate',
    colId: 'rebate',
    headerName: 'Rebate Rate',
    cellRendererSelector: (params) =>
      rateAdapter({
        rate: params.value,
        rateModifier: undefined,
      }),
    comparator: comparator.decimal,
    cellDataType: 'text',
    ...alignRight({ hasPostfix: true }),
  };
}

export function minQuantity(): ColDef<VenueOrderForOms> {
  return {
    ...quantity({
      field: 'minExecutionQuantity',
      colId: 'minExecutionQuantity',
      headerName: 'Min. Quantity',
      dashWhenZero: true,
    }),
    valueFormatter: (params) => {
      if (!params.value || params.value === 1n) return '–';
      return formatPrettyNumber(params.value ?? 0n);
    },
  };
}

export function agreements(): ColDef<VenueOrderForOms> {
  return {
    field: 'agreementIds',
    colId: 'agreementIds',
    headerName: 'Agreements',
    cellRendererSelector: (params) =>
      agreementPopoverAdapter({
        agreementIds: params.value,
      }),
    sortable: false,
    ...alignLeft(),
  };
}
